import { useEffect, useState } from "react";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { FeedbackFilter } from "@/components/feedback/FeedbackFilter";
import { FeedbackCard } from "@/components/feedback/FeedbackCard";
import type { Feedback } from "@/types/feedback";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export default function AdminFeedback() {
  const { isLoading: isLoadingAccess } = useAdminAccess();
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(true);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  useEffect(() => {
    if (!isLoadingAccess) {
      loadFeedback();
    }
  }, [isLoadingAccess]);

  const loadFeedback = async () => {
    try {
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (feedbackError) throw feedbackError;

      const transformedData: Feedback[] = await Promise.all((feedbackData || []).map(async (item) => {
        if (!item.user_id) {
          return {
            ...item,
            user: null
          };
        }

        const { data: userData, error: userError } = await supabase
          .from('profiles')
          .select('email, username')
          .eq('id', item.user_id)
          .single();

        if (userError || !userData) {
          return {
            ...item,
            user: null
          };
        }

        return {
          ...item,
          user: {
            username: userData.username || userData.email?.split('@')[0] || null,
            email: userData.email
          }
        };
      }));

      setFeedback(transformedData);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    setProcessingId(id);
    try {
      if (newStatus === 'rejected') {
        const { error } = await supabase
          .from('feedback')
          .delete()
          .eq('id', id);

        if (error) throw error;
        toast.success('Feedback deleted');
      } else {
        const { error } = await supabase
          .from('feedback')
          .update({ 
            status: newStatus,
            resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null
          })
          .eq('id', id);

        if (error) throw error;
        toast.success(`Feedback marked as ${newStatus}`);
      }
      
      await loadFeedback();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setProcessingId(id);
    try {
      const { error } = await supabase
        .from('feedback')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Feedback deleted');
      await loadFeedback();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const filteredFeedback = feedback.filter(item => {
    const matchesType = filterType === "all" || item.type === filterType;
    const matchesStatus = filterStatus === "all" || item.status === filterStatus;
    return matchesType && matchesStatus;
  });

  if (isLoadingAccess || isLoadingFeedback) {
    return (
      <div className="container mx-auto py-8 px-4 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout title="Feedback Management">
      <FeedbackFilter
        filterType={filterType}
        filterStatus={filterStatus}
        onFilterTypeChange={setFilterType}
        onFilterStatusChange={setFilterStatus}
      />

      <div className="space-y-4">
        {filteredFeedback.map((item) => (
          <FeedbackCard
            key={item.id}
            feedback={item}
            processingId={processingId}
            onStatusUpdate={handleStatusUpdate}
            onDelete={handleDelete}
          />
        ))}

        {filteredFeedback.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No feedback submissions found</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
